import React from 'react';
import { Box, Card, CardContent, Skeleton, Grid } from '@mui/material';

// Card Skeleton for event cards, etc.
export const CardSkeleton = ({ count = 3 }) => {
  return (
    <Grid container spacing={3}>
      {[...Array(count)].map((_, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <Card>
            <Skeleton variant="rectangular" height={200} />
            <CardContent>
              <Skeleton variant="text" width="60%" height={32} />
              <Skeleton variant="text" width="100%" />
              <Skeleton variant="text" width="100%" />
              <Box mt={2} display="flex" gap={1}>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width="40%" />
              </Box>
              <Box mt={1} display="flex" gap={1}>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width="60%" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

// Table Skeleton
export const TableSkeleton = ({ rows = 5, columns = 4 }) => {
  return (
    <Box>
      {[...Array(rows)].map((_, rowIndex) => (
        <Box
          key={rowIndex}
          display="flex"
          gap={2}
          p={2}
          borderBottom="1px solid"
          borderColor="divider"
        >
          {[...Array(columns)].map((_, colIndex) => (
            <Skeleton
              key={colIndex}
              variant="text"
              width={colIndex === 0 ? '30%' : '20%'}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
};

// List Skeleton
export const ListSkeleton = ({ rows = 5 }) => {
  return (
    <Box>
      {[...Array(rows)].map((_, index) => (
        <Box
          key={index}
          display="flex"
          alignItems="center"
          gap={2}
          p={2}
          borderBottom="1px solid"
          borderColor="divider"
        >
          <Skeleton variant="circular" width={40} height={40} />
          <Box flexGrow={1}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="60%" />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

// Dashboard Stats Skeleton
export const StatCardSkeleton = ({ count = 4 }) => {
  return (
    <Grid container spacing={3}>
      {[...Array(count)].map((_, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Card>
            <CardContent>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" height={48} />
              <Skeleton variant="text" width="50%" />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

// Detail Page Skeleton
export const DetailSkeleton = () => {
  return (
    <Box>
      <Skeleton variant="rectangular" width="100%" height={400} sx={{ mb: 3 }} />
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Skeleton variant="text" width="80%" height={48} />
              <Skeleton variant="text" width="100%" />
              <Skeleton variant="text" width="100%" />
              <Skeleton variant="text" width="100%" />
              <Skeleton variant="text" width="90%" />
              <Box mt={3}>
                <Skeleton variant="text" width="60%" height={32} />
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="100%" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Skeleton variant="text" width="60%" height={32} />
              <Box mt={2}>
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="80%" />
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Skeleton variant="rectangular" width="100%" height={120} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// Form Skeleton
export const FormSkeleton = ({ fields = 6 }) => {
  return (
    <Box>
      <Skeleton variant="text" width="40%" height={40} sx={{ mb: 3 }} />
      {[...Array(fields)].map((_, index) => (
        <Box key={index} mb={3}>
          <Skeleton variant="text" width="30%" height={24} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={56} />
        </Box>
      ))}
      <Box display="flex" gap={2} mt={4}>
        <Skeleton variant="rectangular" width={120} height={42} />
        <Skeleton variant="rectangular" width={120} height={42} />
      </Box>
    </Box>
  );
};

// Generic Page Skeleton
export const PageSkeleton = () => {
  return (
    <Box>
      <Box mb={4}>
        <Skeleton variant="text" width="40%" height={48} />
        <Skeleton variant="text" width="60%" />
      </Box>
      <CardSkeleton count={6} />
    </Box>
  );
};

export default {
  CardSkeleton,
  TableSkeleton,
  ListSkeleton,
  StatCardSkeleton,
  DetailSkeleton,
  FormSkeleton,
  PageSkeleton,
};
